import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import { View, Mask } from '@smarterservices/smarter-ui';
import Loader from '@ui/context/session/components/Common/Loader';
import { debounce } from '@instructure/debounce';
import VideoMagnifier from '../../Common/VideoMagnifier';
import { useAppStore, getStore, getStoreRaw } from '../../../stores/StoreContext';
import './styles.css';

// Static style objects hoisted outside component to prevent new references on every render
const VIEWPORT_STYLE = Object.freeze({
  overflow: 'hidden',
  contain: 'strict',
  transition: 'border-color 0.2s ease-in-out',
});

const MASK_THEME_OVERRIDE = Object.freeze({
  borderRadius: '0rem',
  background: 'rgba(255, 255, 255, 0.9)',
  zIndex: 7000,
});

/**
 * Get the total spacing (margin + border + padding) for a given CSS class
 * @param {string} className - The CSS class name to analyze
 * @returns {Object} Object with horizontal and vertical spacing values
 */
const getClassSpacing = (className) => {
  if (typeof document === 'undefined') {
    return { horizontal: 0, vertical: 0 };
  }

  const tempElement = document.createElement('div');
  tempElement.className = className;
  tempElement.style.visibility = 'hidden';
  tempElement.style.position = 'absolute';
  tempElement.style.top = '-9999px';
  document.body.appendChild(tempElement);

  try {
    const computedStyle = window.getComputedStyle(tempElement);
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
    const marginRight = parseFloat(computedStyle.marginRight) || 0;
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

    const horizontal = marginLeft + marginRight;
    const vertical = marginTop + marginBottom;

    return { horizontal, vertical };
  } finally {
    document.body.removeChild(tempElement);
  }
};

/**
 * WebRTC video renderer component that displays a LiveKit track in a standard HTML video element.
 * 
 * This component handles:
 * - Attaching LiveKit video tracks to HTML video elements
 * - Detecting and displaying reconnection states when video freezes
 * - Monitoring connection quality and video playback health
 * - Managing video magnification settings
 * - Reporting track status changes to parent components
 * 
 * Performance: Uses lightweight event listeners and minimal polling (1 interval per video)
 * for freeze detection with negligible CPU overhead (<0.01% of video rendering cost).
 * 
 * @param {Object} props
 * @param {Object} props.SessionTrack - The SessionTrack object containing the LiveKit track reference
 * @param {Function} props.onTrackStatusChange - Callback when track connection status changes
 */
const Webrtc = ({ SessionTrack, onTrackStatusChange }) => {
  // Refs for DOM elements
  const videoRef = useRef(null); // Reference to the HTML video element
  const containerRef = useRef(null); // Reference to the container div

  // Video state management
  const [, setClassSpacing] = useState({ horizontal: 0, vertical: 0 }); // CSS spacing calculations
  const [isVideoReady, setIsVideoReady] = useState(false); // True when video metadata is loaded and ready to display
  const [, setVideoMetadata] = useState({ width: 1920, height: 1080 }); // Video dimensions
  const [isTrackSubscribed, setIsTrackSubscribed] = useState(true); // LiveKit subscription status

  // Reconnection state management
  const hasLoadedOnceRef = useRef(false); // Tracks if stream has successfully loaded at least once (used to differentiate initial load vs reconnection)
  const [showReconnecting, setShowReconnecting] = useState(false); // True when showing "Attempting to reconnect..." message
  const showReconnectingRef = useRef(false); // Ref to track reconnecting state for use in closures
  const reconnectingTimeoutRef = useRef(null); // Timeout for debouncing reconnecting state changes

  // Freeze detection state
  const videoFreezeTimeoutRef = useRef(null); // Timeout for detecting video freeze after 'waiting' event
  const lastFrameTimeRef = useRef(Date.now()); // Timestamp of last video frame update (used for polling-based freeze detection)
  const lastVideoTimeRef = useRef(0); // Last known video currentTime for freeze detection
  const freezeCheckIntervalRef = useRef(null); // Reference to freeze check interval for cleanup
  const previousTrackSidRef = useRef(null); // Track the previous track SID to detect actual track changes
  const isActuallyPlayingRef = useRef(false); // Track if video is confirmed playing to prevent false reconnection states

  // Get stores
  const PlayerStore = getStore('playerStore');
  const ExamStore = getStore('examSessionStore');
  const appStoreRef = getStoreRaw('appStore'); // Get raw Zustand store for use in callbacks

  // Get magnification settings from store — select primitives to avoid re-renders from object reference changes
  const magnificationEnabled = useAppStore((state) => state.magnification?.enabled);
  const magnificationLevel = useAppStore((state) => state.magnification?.level);
  const magnificationLensSize = useAppStore((state) => state.magnification?.lensSize);
  const isRoomConnected = useAppStore((state) => state.isRoomConnected);

  const handleLevelChange = useCallback((newLevel) => {
    const currentMagnification = appStoreRef.getState().magnification;
    appStoreRef.getState().updateMagnification({ ...currentMagnification, level: newLevel });
  }, [appStoreRef]);

  const handleLensSizeChange = useCallback((newSize) => {
    const currentMagnification = appStoreRef.getState().magnification;
    appStoreRef.getState().updateMagnification({ ...currentMagnification, lensSize: newSize });
  }, [appStoreRef]);


  /**
   * Clear any overlays when video is ready to display.
   * This ensures loading overlays don't persist after video loads.
   */
  useEffect(() => {
    PlayerStore.setOverlay(null);
  }, [isVideoReady]);

  /**
   * Calculate CSS spacing for the video container on mount.
   * This is used for proper video element positioning.
   */
  useEffect(() => {
    const spacing = getClassSpacing('video-container-wrapper-mask');
    setClassSpacing(spacing);
  }, []);

  /**
   * Fallback timeout to hide loader if video doesn't load within 3 seconds.
   * Only runs on initial load, not during reconnection attempts.
   * This prevents infinite loading states if metadata events don't fire.
   */
  useEffect(() => {
    if (isRoomConnected && !isVideoReady && !showReconnecting && !hasLoadedOnceRef.current) {
      const timeout = setTimeout(() => {
        setIsVideoReady(true);
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [isRoomConnected, isVideoReady, showReconnecting]);

  /**
   * Monitor LiveKit track subscription state.
   * 
   * Tracks when the video track is subscribed/unsubscribed and reports status changes.
   * The subscription status can change when:
   * - Initial connection is established
   * - Network disconnects/reconnects
   * - Track is manually unsubscribed
   */
  useEffect(() => {
    const publication = SessionTrack?.webrtcRef?.publication;
    if (!publication) {
      // Don't report disconnected if publication doesn't exist yet (still loading)
      // Just set local state to true to show loading state
      setIsTrackSubscribed(true);
      return;
    }

    // Check initial subscription state - if track exists and is subscribed, consider it connected
    // During initial load, wait for track to be fully ready before reporting status
    const initialSubscribed = publication.track && publication.isSubscribed;
    setIsTrackSubscribed(initialSubscribed);

    // Only report status if we have a definitive state (not during initial uncertain state)
    if (initialSubscribed && onTrackStatusChange) {
      onTrackStatusChange('connected');
    }

    // Listen for subscription state changes - this fires when track actually disconnects
    const handleSubscriptionStatusChanged = () => {
      // When subscription status changes, check if track still exists and is subscribed
      const isSubscribed = publication.track && publication.isSubscribed;
      // console.log(`[Webrtc ${new Date().toISOString()}] Subscription status changed:`, isSubscribed, 'hasLoadedOnce:', hasLoadedOnceRef.current, 'trackId:', SessionTrack?.id);
      setIsTrackSubscribed(isSubscribed);

      // Show reconnecting overlay if track becomes unsubscribed and we've loaded before
      if (!isSubscribed && hasLoadedOnceRef.current) {
        // console.log(`[Webrtc ${new Date().toISOString()}] SHOWING RECONNECTING OVERLAY - Subscription lost`);
        isActuallyPlayingRef.current = false;
        showReconnectingRef.current = true;
        setShowReconnecting(true);
        setIsVideoReady(false);
      }

      if (onTrackStatusChange) {
        onTrackStatusChange(isSubscribed ? 'connected' : 'disconnected');
      }
    };

    if (publication?.on) {
      publication.on('subscriptionStatusChanged', handleSubscriptionStatusChanged);
    } else {
      // TODO: log that there is no publication to listen to ...
    }

    return () => {
      if (publication?.off) {
        publication.off('subscriptionStatusChanged', handleSubscriptionStatusChanged);
      }
    };
  }, [SessionTrack?.webrtcRef?.publication, onTrackStatusChange]);

  /**
   * Monitor participant speaking state for noise detection.
   * 
   * Only tracks speaking events for camera tracks (not screenshare) to avoid
   * duplicate noise events from the same participant.
   */
  useEffect(() => {
    const participant = SessionTrack?.webrtcRef?.participant;

    // Listen for speaking state changes
    const handleHasNoiseChanged = (speaking) => {
      // Only monitor speaking for camera tracks, not screenshare
      // This prevents duplicate noise events from the same participant
      if (SessionTrack?.source !== 'camera') {
        return;
      }
      ExamStore.setNoise({ hasNoise: speaking, participant });
    };

    if (!participant) return;

    participant.on('isSpeakingChanged', handleHasNoiseChanged);

    return () => {
      participant.off('isSpeakingChanged', handleHasNoiseChanged);
    };
  }, [SessionTrack?.webrtcRef?.participant]);

  /**
   * Monitor LiveKit participant connection quality for fast disconnection detection.
   * 
   * This provides the fastest indication of connection issues (typically <1 second).
   * When quality drops to 'lost', immediately shows reconnecting state.
   * 
   * Performance: Event-driven, only fires when quality changes (rare).
   */
  useEffect(() => {
    const participant = SessionTrack?.webrtcRef?.participant;
    if (!participant) return;

    const handleConnectionQualityChanged = (quality) => {
      // console.log(`[Webrtc ${new Date().toISOString()}] Connection quality changed:`, quality, 'hasLoadedOnce:', hasLoadedOnceRef.current, 'trackId:', SessionTrack?.id);
      // If connection quality is lost and stream has loaded before, show reconnecting
      if (quality === 'lost' && hasLoadedOnceRef.current) {
        // console.log(`[Webrtc ${new Date().toISOString()}] SHOWING RECONNECTING OVERLAY - Connection quality lost`);
        // Reset playing state since connection is lost
        isActuallyPlayingRef.current = false;
        showReconnectingRef.current = true;
        setShowReconnecting(true);
        setIsVideoReady(false);
        if (onTrackStatusChange) {
          onTrackStatusChange('disconnected');
        }
      }
      // If connection quality improves from lost, clear reconnecting state
      else if (quality === 'excellent' || quality === 'good' || quality === 'poor') {
        // Clear any pending reconnecting timeouts
        if (reconnectingTimeoutRef.current) {
          clearTimeout(reconnectingTimeoutRef.current);
          reconnectingTimeoutRef.current = null;
        }
        // Only update state if currently showing reconnecting
        if (showReconnectingRef.current) {
          showReconnectingRef.current = false;
          setShowReconnecting(false);
          setIsVideoReady(true);
          if (onTrackStatusChange) {
            onTrackStatusChange('connected');
          }
        }
      }
    };

    participant.on('connectionQualityChanged', handleConnectionQualityChanged);

    return () => {
      participant.off('connectionQualityChanged', handleConnectionQualityChanged);
    };
  }, [SessionTrack?.webrtcRef?.participant, onTrackStatusChange]);

  /**
   * Monitor HTML video element events for freeze/stall detection.
   * 
   * Implements multi-layered freeze detection:
   * 1. 'stalled' event - Network stalled while fetching media (immediate)
   * 2. 'waiting' event - Playback stopped due to lack of data (3 second grace period)
   * 3. 'playing' event - Video resumed, clears reconnecting state
   * 4. 'timeupdate' event - Tracks video is alive (updates lastFrameTime)
   * 5. Polling - Checks if video hasn't updated in 3 seconds (runs every 1 second)
   * 
   * This catches frozen video within 1-3 seconds, much faster than waiting for
   * LiveKit's subscription status to change (which can take 15+ seconds).
   * 
   * Performance: 
   * - Event listeners are passive (no CPU until events fire)
   * - Polling does simple timestamp comparison (~0.001ms per second per video)
   * - Total overhead is <0.01% of video rendering cost
   */
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // console.log(`[Webrtc ${new Date().toISOString()}] Setting up freeze detection for trackId:`, SessionTrack?.id, 'hasLoadedOnce:', hasLoadedOnceRef.current);

    const handleVideoStalled = () => {
      // Network stalled while fetching media data.
      // With adaptive streaming, stalled events fire during normal quality/layer
      // switches — showing the overlay immediately causes a visible flash that
      // looks like freezing even though the stream recovers in <1 second.
      // Use a 3-second grace period (same as the 'waiting' handler) so brief
      // stalls are invisible to the user.
      if (hasLoadedOnceRef.current && !isActuallyPlayingRef.current) {
        if (videoFreezeTimeoutRef.current) {
          clearTimeout(videoFreezeTimeoutRef.current);
        }
        videoFreezeTimeoutRef.current = setTimeout(() => {
          if (!isActuallyPlayingRef.current) {
            showReconnectingRef.current = true;
            setShowReconnecting(true);
            setIsVideoReady(false);
          }
        }, 3000);
      }
    };

    const handleVideoWaiting = () => {
      // Playback stopped due to lack of data - possible freeze
      // Give it 3 seconds grace period before showing reconnecting state
      if (hasLoadedOnceRef.current && !isActuallyPlayingRef.current) {
        if (videoFreezeTimeoutRef.current) {
          clearTimeout(videoFreezeTimeoutRef.current);
        }
        videoFreezeTimeoutRef.current = setTimeout(() => {
          showReconnectingRef.current = true;
          setShowReconnecting(true);
          setIsVideoReady(false);
        }, 3000);
      }
    };

    const handleVideoPlaying = () => {
      // Video is playing again - clear all freeze detection state
      // This is the most reliable indicator that video has recovered
      isActuallyPlayingRef.current = true;

      if (videoFreezeTimeoutRef.current) {
        clearTimeout(videoFreezeTimeoutRef.current);
        videoFreezeTimeoutRef.current = null;
      }
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
        reconnectingTimeoutRef.current = null;
      }

      lastFrameTimeRef.current = Date.now();
      lastVideoTimeRef.current = videoElement.currentTime;

      // Clear reconnecting state - video is confirmed playing
      if (showReconnectingRef.current) {
        showReconnectingRef.current = false;
        setShowReconnecting(false);
        setIsVideoReady(true);
        if (onTrackStatusChange) {
          onTrackStatusChange('connected');
        }
      }
    };

    const handleVideoTimeUpdate = () => {
      // Video time is updating - update last frame timestamp for polling detection
      lastFrameTimeRef.current = Date.now();
      // NOTE: Don't clear reconnecting state here - wait for 'playing' event
      // timeupdate can fire even when video is frozen, causing state flickering
    };

    const handleVideoEmptied = () => {
      // Video has been emptied - track likely disconnected
      if (hasLoadedOnceRef.current) {
        isActuallyPlayingRef.current = false;
        showReconnectingRef.current = true;
        setShowReconnecting(true);
        setIsVideoReady(false);
      }
    };

    // Note: 'suspend' event is NOT monitored. It fires when the browser
    // intentionally stops loading data because it has enough buffered —
    // this is normal browser behavior, not a connection problem. With
    // adaptive streaming it fires during quality switches, causing false
    // positive reconnection overlays. The 2-second polling interval is
    // a sufficient fallback for detecting genuine freezes.
    videoElement.addEventListener('stalled', handleVideoStalled);
    videoElement.addEventListener('waiting', handleVideoWaiting);
    videoElement.addEventListener('playing', handleVideoPlaying);
    videoElement.addEventListener('timeupdate', handleVideoTimeUpdate);
    videoElement.addEventListener('emptied', handleVideoEmptied);

    // Polling-based freeze detection as fallback
    // Checks if video currentTime is actually progressing
    // This is more reliable than timeupdate events which may not fire regularly for sidebar videos
    freezeCheckIntervalRef.current = setInterval(() => {
      const currentVideoTime = videoElement.currentTime;
      const timeSinceLastCheck = Date.now() - lastFrameTimeRef.current;
      const readyState = videoElement.readyState;

      // Log polling status for debugging
      // console.log(`[Webrtc ${new Date().toISOString()}] Polling check - currentTime:`, currentVideoTime, 'lastTime:', lastVideoTimeRef.current, 'readyState:', readyState, 'timeSince:', timeSinceLastCheck, 'trackId:', SessionTrack?.id);

      // Check if video time has progressed since last check
      if (currentVideoTime > lastVideoTimeRef.current) {
        // Video is progressing normally, update tracking
        lastVideoTimeRef.current = currentVideoTime;
        lastFrameTimeRef.current = Date.now();
        isActuallyPlayingRef.current = true;

        // Clear any pending reconnecting state if video is progressing
        // Use ref to avoid stale closure issues
        if (showReconnectingRef.current) {
          // console.log(`[Webrtc ${new Date().toISOString()}] CLEARING RECONNECTING OVERLAY - Video progressing, trackId:`, SessionTrack?.id);
          showReconnectingRef.current = false;
          setShowReconnecting(false);
          setIsVideoReady(true);
          if (onTrackStatusChange) {
            onTrackStatusChange('connected');
          }
        }
      } else if (timeSinceLastCheck > 3000 &&
        hasLoadedOnceRef.current &&
        !showReconnectingRef.current &&
        !videoElement.paused &&
        !videoElement.ended &&
        currentVideoTime > 0 &&
        !isActuallyPlayingRef.current) {
        // Video appears frozen - currentTime hasn't progressed in 3 seconds while it should be playing
        // Reduced from 5 to 3 seconds for faster detection
        // Check readyState - if it's low, definitely show overlay
        const hasLowReadyState = videoElement.readyState < 2;
        if (hasLowReadyState || timeSinceLastCheck > 5000) {
          // console.log(`[Webrtc ${new Date().toISOString()}] SHOWING RECONNECTING OVERLAY - Video frozen, readyState:`, videoElement.readyState, 'timeSince:', timeSinceLastCheck);
          showReconnectingRef.current = true;
          setShowReconnecting(true);
          setIsVideoReady(false);
          isActuallyPlayingRef.current = false;
        }
      }
    }, 2000);

    return () => {
      videoElement.removeEventListener('stalled', handleVideoStalled);
      videoElement.removeEventListener('waiting', handleVideoWaiting);
      videoElement.removeEventListener('playing', handleVideoPlaying);
      videoElement.removeEventListener('timeupdate', handleVideoTimeUpdate);
      videoElement.removeEventListener('emptied', handleVideoEmptied);

      if (freezeCheckIntervalRef.current) {
        clearInterval(freezeCheckIntervalRef.current);
        freezeCheckIntervalRef.current = null;
      }
      if (videoFreezeTimeoutRef.current) {
        clearTimeout(videoFreezeTimeoutRef.current);
        videoFreezeTimeoutRef.current = null;
      }
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
        reconnectingTimeoutRef.current = null;
      }
    };
  }, [onTrackStatusChange]);

  /**
   * Attach LiveKit video track to HTML video element and load metadata.
   * 
   * This is the core effect that:
   * 1. Attaches the LiveKit track to the video element
   * 2. Sets up metadata and playback event listeners
   * 3. Manages the reconnecting state when track changes
   * 4. Cleans up on unmount or track change
   * 
   * The effect re-runs when:
   * - SessionTrack changes (new track object)
   * - Track publication changes (reconnection with new SID)
   * - Room connection state changes
   * - Track subscription status changes
   * 
   * Note: SessionTrack.lastUpdated is NOT in dependencies to prevent flickering.
   * Component remounting is handled by React keys in parent components.
   */
  useEffect(() => {
    const videoTrack = SessionTrack?.webrtcRef?.publication?.track;
    const currentTrackSid = SessionTrack?.webrtcRef?.publication?.trackSid;

    // Only show reconnecting if:
    // 1. Stream has loaded before (hasLoadedOnceRef.current = true)
    // 2. AND the track SID has actually changed (indicating a real reconnection, not just an effect re-run)
    // 3. AND we have a previous track SID (not initial load)
    // 4. AND the video is not currently playing (prevents false positives during normal operation)
    // This prevents false reconnection states when pulling sessions from waiting room or switching views
    const isTrackSidChanged = hasLoadedOnceRef.current &&
      previousTrackSidRef.current &&
      currentTrackSid !== previousTrackSidRef.current &&
      !isActuallyPlayingRef.current;

    if (isTrackSidChanged) {
      // Reset playing state for new track
      isActuallyPlayingRef.current = false;
      showReconnectingRef.current = true;
      setShowReconnecting(true);
      setIsVideoReady(false);
    } else if (!isTrackSidChanged && previousTrackSidRef.current === currentTrackSid) {
      // Same track, don't reset video ready state
      // This prevents flickering when component re-renders
    } else {
      // New track or initial load - reset state
      setIsVideoReady(false);
      isActuallyPlayingRef.current = false;
    }

    // Update the previous track SID for next comparison
    previousTrackSidRef.current = currentTrackSid;

    // Track whether we successfully attached so cleanup always detaches
    let didAttach = false;
    let metadataHandler = null;
    let canPlayHandler = null;

    if (videoTrack && videoRef.current) {
      // Attach the track to the video element
      // LiveKit's attach() internally creates/reuses a MediaStream, sets srcObject,
      // and calls element.play() — no need for a separate play() call.
      videoTrack.attach(videoRef.current);
      didAttach = true;

      // Handle video metadata loaded event
      metadataHandler = () => {
        if (videoRef.current) {
          const width = videoRef.current.videoWidth || 1920;
          const height = videoRef.current.videoHeight || 1080;
          setVideoMetadata({ width, height });
          setIsVideoReady(true);
          hasLoadedOnceRef.current = true; // Mark that stream has loaded successfully (for reconnection detection)

          // Clear all reconnection-related timeouts and state
          if (reconnectingTimeoutRef.current) {
            clearTimeout(reconnectingTimeoutRef.current);
            reconnectingTimeoutRef.current = null;
          }
          if (videoFreezeTimeoutRef.current) {
            clearTimeout(videoFreezeTimeoutRef.current);
            videoFreezeTimeoutRef.current = null;
          }

          showReconnectingRef.current = false;
          setShowReconnecting(false); // Clear reconnecting state - video is ready

          // Report connected status
          if (onTrackStatusChange) {
            onTrackStatusChange('connected');
          }
        }
      };

      // Handle canplay event as fallback for metadata loading
      canPlayHandler = () => {
        // Sometimes loadedmetadata doesn't fire, so we check dimensions here too
        if (videoRef.current && videoRef.current.videoWidth && videoRef.current.videoHeight) {
          metadataHandler();
        }
      };

      videoRef.current.addEventListener('loadedmetadata', metadataHandler);
      videoRef.current.addEventListener('canplay', canPlayHandler);

      // Check if metadata is already loaded (for track switches)
      if (videoRef.current.videoWidth && videoRef.current.videoHeight) {
        metadataHandler();
      }
    }

    // Single cleanup path — always detach track and remove listeners.
    // Previously there were two separate return statements which meant
    // the track was never detached when it was valid, causing stale
    // MediaStream attachments and play() races on re-runs.
    return () => {
      if (videoRef.current) {
        if (metadataHandler) videoRef.current.removeEventListener('loadedmetadata', metadataHandler);
        if (canPlayHandler) videoRef.current.removeEventListener('canplay', canPlayHandler);
      }
      // Always detach the track that was attached in THIS effect run
      if (didAttach && videoTrack) {
        videoTrack.detach(videoRef.current);
      }
    };
    // Dependencies: only re-run when the actual track object changes.
    // isTrackSubscribed and isRoomConnected are NOT included because:
    // - Subscription changes are already handled by the subscription effect (line ~160)
    // - Room connection changes don't require track re-attachment
    // Including them caused detach/reattach cycles during brief network blips,
    // producing visible freezes (black frames between detach and reattach).
  }, [SessionTrack, SessionTrack?.webrtcRef?.publication?.track]);


  return (
    <View
      as="div"
      position="relative"
      id={SessionTrack?.id || 'viewport'}
      className="webrtc-viewport"
      margin="auto"
      background="primary-inverse"
      width="100%"
      height="100%"
      elementRef={containerRef}
      style={VIEWPORT_STYLE}
      withVisualDebug={false}
    >
      {/* Show loading spinner while video metadata is loading and track is subscribed */}
      {
        !isVideoReady && (isTrackSubscribed || showReconnecting) && (
          <Mask
            placement="center"
            themeOverride={MASK_THEME_OVERRIDE}
          >
            <Loader spinnerSize="small"
              color="primary"
              textSize="medium"
              text={showReconnecting ? 'Attempting to reconnect...' : 'Loading stream...'} />
          </Mask>
        )
      }

      <VideoMagnifier
        mediaRef={videoRef}
        enabled={magnificationEnabled}
        level={magnificationLevel}
        lensSize={magnificationLensSize}
        onLevelChange={handleLevelChange}
        onLensSizeChange={handleLensSizeChange}
      >

        <div
          className="video-container-wrapper-mask"
        >
          <video
            ref={videoRef}
            className="session-video-element webrtc"
            muted
            playsInline
            autoPlay
          />

        </div>
      </VideoMagnifier>
    </View >
  );
};

const MemoizedWebrtc = memo(Webrtc, (prevProps, nextProps) => {
  return (
    prevProps.SessionTrack?.id === nextProps.SessionTrack?.id &&
    prevProps.SessionTrack?.lastUpdated === nextProps.SessionTrack?.lastUpdated &&
    prevProps.onTrackStatusChange === nextProps.onTrackStatusChange
  );
});

export default MemoizedWebrtc;
